// src/features/project/SprintsPanel.tsx
// Sprint lifecycle: ListSprints + Create / Update / Start / Complete / Clone / Delete,
// and an inline burndown (GetSprintBurndown). Sprint contents are filled from the
// backlog / item rail (AddItemToSprint / RemoveItemFromSprint live there).

import { useEffect, useMemo, useState } from 'react';
import { observer } from '@react';
import { useI18n, useProjectSprint } from '@react/hooks';
import { Button, Input, Textarea, Card, CardContent, Badge, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ProjectModal, Field, ProgressBar } from './ProjectModal';
import { BurndownChart } from './charts';
import { idOf, listParams, toTimestamp, toDateInputValue } from './meta';
import { getStatusMapping, sprintStatusMap, formatDate } from './logic';
import type { Entity } from '@core/types/entity';
import type { Sprint, BurndownData } from '@core/types/project';

interface SprintsPanelProps {
  projectEntity: Entity;
  onChanged: () => void;
}

const STATUS_ORDER: Record<string, number> = {
  SPRINT_STATUS_ACTIVE: 0,
  SPRINT_STATUS_PLANNING: 1,
  SPRINT_STATUS_COMPLETED: 2,
  SPRINT_STATUS_CANCELLED: 3,
};

export const SprintsPanel = observer(({ projectEntity, onChanged }: SprintsPanelProps) => {
  const { t, locale } = useI18n();
  const store = useProjectSprint();
  const projectId = projectEntity.id;

  const [editing, setEditing] = useState<Sprint | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [completing, setCompleting] = useState<Sprint | null>(null);
  const [deleting, setDeleting] = useState<Sprint | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = () => store.ListSprints(projectId, listParams());
  useEffect(() => {
    reload();
  }, [projectId]);

  const sprints: Sprint[] = store.items ?? [];
  const sorted = useMemo(
    () => [...sprints].sort((a, b) => (STATUS_ORDER[a.status ?? ''] ?? 9) - (STATUS_ORDER[b.status ?? ''] ?? 9)),
    [sprints],
  );

  const afterMutation = () => {
    reload();
    onChanged();
  };

  const start = (s: Sprint) =>
    withBusy(() => store.StartSprint(projectId, idOf(s), { project: projectEntity, sprint: s.entity! } as never));
  const clone = (s: Sprint) =>
    withBusy(() =>
      store.CloneSprint(projectId, idOf(s), {
        project: projectEntity,
        sprint: s.entity!,
        carry_incomplete_items: true,
      } as never),
    );
  const del = () =>
    deleting &&
    withBusy(async () => {
      await store.DeleteSprint(projectId, idOf(deleting));
      setDeleting(null);
    });

  async function withBusy(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      afterMutation();
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">{t('project.sprints.title')}</h2>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
        >
          {t('project.sprints.create')}
        </Button>
      </div>

      {!!store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {sorted.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-lg font-medium text-foreground">{t('project.sprints.empty')}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t('project.sprints.emptyDescription')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map((s) => (
            <SprintCard
              key={idOf(s)}
              projectId={projectId}
              sprint={s}
              busy={busy}
              locale={locale}
              onStart={() => start(s)}
              onClone={() => clone(s)}
              onEdit={() => {
                setEditing(s);
                setShowForm(true);
              }}
              onComplete={() => setCompleting(s)}
              onDelete={() => setDeleting(s)}
            />
          ))}
        </div>
      )}

      {showForm && (
        <SprintFormDialog
          projectEntity={projectEntity}
          sprint={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            afterMutation();
          }}
        />
      )}

      {completing && (
        <CompleteSprintDialog
          projectEntity={projectEntity}
          sprint={completing}
          otherSprints={sprints.filter((x) => idOf(x) !== idOf(completing) && x.status !== 'SPRINT_STATUS_COMPLETED')}
          onClose={() => setCompleting(null)}
          onDone={() => {
            setCompleting(null);
            afterMutation();
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        title={t('project.sprints.deleteTitle')}
        description={t('project.sprints.deleteDesc')}
        destructive
        isConfirming={busy}
        onConfirm={del}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
});

SprintsPanel.displayName = 'SprintsPanel';

// ---------------------------------------------------------------------------
// Sprint card
// ---------------------------------------------------------------------------

const SprintCard = observer(function SprintCard({
  projectId,
  sprint,
  busy,
  locale,
  onStart,
  onClone,
  onEdit,
  onComplete,
  onDelete,
}: {
  projectId: string;
  sprint: Sprint;
  busy: boolean;
  locale: string;
  onStart: () => void;
  onClone: () => void;
  onEdit: () => void;
  onComplete: () => void;
  onDelete: () => void;
}) {
  const { t } = useI18n();
  const store = useProjectSprint();
  const status = getStatusMapping(sprintStatusMap, sprint.status);
  const isActive = sprint.status === 'SPRINT_STATUS_ACTIVE';
  const isPlanning = sprint.status === 'SPRINT_STATUS_PLANNING';
  const isCompleted = sprint.status === 'SPRINT_STATUS_COMPLETED';
  const progress = sprint.total_items ? Math.round((sprint.completed_items / sprint.total_items) * 100) : 0;

  const [burndown, setBurndown] = useState<BurndownData | null>(null);
  const [showBurndown, setShowBurndown] = useState(false);
  const [loadingBd, setLoadingBd] = useState(false);

  const toggleBurndown = async () => {
    if (showBurndown) {
      setShowBurndown(false);
      return;
    }
    setShowBurndown(true);
    if (!burndown) {
      setLoadingBd(true);
      try {
        const res = await store.GetSprintBurndown(projectId, idOf(sprint));
        setBurndown(res.burndown ?? null);
      } catch {
        /* store surfaces error */
      } finally {
        setLoadingBd(false);
      }
    }
  };

  return (
    <Card className={isActive ? 'ring-2 ring-primary/20' : undefined}>
      <CardContent className="p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate text-sm font-semibold text-foreground">{sprint.name}</h3>
              <Badge variant={status.variant as never} size="sm">
                {t(status.labelKey)}
              </Badge>
              {isActive && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                  {t('project.sprints.currentSprint')}
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              {formatDate(sprint.start_date, locale) !== '--' && (
                <span>
                  {formatDate(sprint.start_date, locale)}
                  {formatDate(sprint.end_date, locale) !== '--' && ` – ${formatDate(sprint.end_date, locale)}`}
                </span>
              )}
              {sprint.goal && <span className="truncate">{sprint.goal}</span>}
            </div>
            <div className="mt-3 flex items-center gap-3">
              <div className="w-40">
                <ProgressBar percent={progress} />
              </div>
              <span className="text-xs text-muted-foreground">
                {sprint.completed_items ?? 0}/{sprint.total_items ?? 0} · {sprint.completed_points ?? 0}/
                {sprint.planned_points ?? 0} {t('project.sprints.points')}
              </span>
            </div>
          </div>

          <div className="flex flex-shrink-0 flex-wrap gap-2">
            {isPlanning && (
              <Button size="sm" onClick={onStart} disabled={busy}>
                {t('project.sprints.start')}
              </Button>
            )}
            {isActive && (
              <Button size="sm" onClick={onComplete} disabled={busy}>
                {t('project.sprints.complete')}
              </Button>
            )}
            {isCompleted && (
              <Button size="sm" variant="secondary" onClick={onClone} disabled={busy}>
                {t('project.sprints.clone')}
              </Button>
            )}
            {(isActive || isCompleted) && (
              <Button size="sm" variant="ghost" onClick={toggleBurndown} disabled={busy}>
                {t('project.sprints.burndown')}
              </Button>
            )}
            {!isCompleted && (
              <Button size="sm" variant="secondary" onClick={onEdit} disabled={busy}>
                {t('common.edit')}
              </Button>
            )}
            {isPlanning && (
              <Button size="sm" variant="danger" onClick={onDelete} disabled={busy}>
                {t('common.delete')}
              </Button>
            )}
          </div>
        </div>

        {showBurndown && (
          <div className="mt-4 border-t border-border pt-4">
            {loadingBd ? (
              <div className="h-40 animate-pulse rounded bg-muted/40" />
            ) : (
              <BurndownChart
                points={burndown?.points ?? []}
                totalPoints={burndown?.total_points ?? 0}
                labels={{
                  title: t('project.analytics.burndown'),
                  ideal: t('project.analytics.ideal'),
                  remaining: t('project.analytics.remaining'),
                  empty: t('project.analytics.noData'),
                }}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
});

// ---------------------------------------------------------------------------
// Create / edit dialog
// ---------------------------------------------------------------------------

function SprintFormDialog({
  projectEntity,
  sprint,
  onClose,
  onSaved,
}: {
  projectEntity: Entity;
  sprint: Sprint | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const store = useProjectSprint();
  const [name, setName] = useState(sprint?.name ?? '');
  const [goal, setGoal] = useState(sprint?.goal ?? '');
  const [startDate, setStartDate] = useState(toDateInputValue(sprint?.start_date));
  const [endDate, setEndDate] = useState(toDateInputValue(sprint?.end_date));
  const [busy, setBusy] = useState(false);

  // Load the freshest copy when editing an existing sprint.
  useEffect(() => {
    if (!sprint) return;
    let alive = true;
    store
      .GetSprint(projectEntity.id, idOf(sprint))
      .then((full) => {
        if (!alive || !full) return;
        setName(full.name ?? '');
        setGoal(full.goal ?? '');
        setStartDate(toDateInputValue(full.start_date));
        setEndDate(toDateInputValue(full.end_date));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      if (sprint) {
        await store.UpdateSprint(projectEntity.id, idOf(sprint), {
          project: projectEntity,
          sprint: {
            ...sprint,
            name: name.trim(),
            goal,
            start_date: toTimestamp(startDate),
            end_date: toTimestamp(endDate),
          },
          field_mask: { paths: ['name', 'goal', 'start_date', 'end_date'] },
        } as never);
      } else {
        await store.CreateSprint(projectEntity.id, {
          project: projectEntity,
          name: name.trim(),
          goal: goal || undefined,
          start_date: toTimestamp(startDate),
          end_date: toTimestamp(endDate),
        } as never);
      }
      onSaved();
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  return (
    <ProjectModal
      open
      title={sprint ? t('project.sprints.editTitle') : t('project.sprints.createTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={busy || !name.trim()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!!store.error && <ErrorAlert error={store.error} />}
        <Field label={t('project.sprints.name')} htmlFor="sprint-name">
          <Input id="sprint-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('project.sprints.goal')} htmlFor="sprint-goal">
          <Textarea id="sprint-goal" value={goal} onChange={(e) => setGoal(e.target.value)} rows={2} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('project.sprints.startDate')} htmlFor="sprint-start">
            <Input id="sprint-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label={t('project.sprints.endDate')} htmlFor="sprint-end">
            <Input id="sprint-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
        </div>
      </div>
    </ProjectModal>
  );
}

// ---------------------------------------------------------------------------
// Complete dialog (move incomplete + retro notes)
// ---------------------------------------------------------------------------

function CompleteSprintDialog({
  projectEntity,
  sprint,
  otherSprints,
  onClose,
  onDone,
}: {
  projectEntity: Entity;
  sprint: Sprint;
  otherSprints: Sprint[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const store = useProjectSprint();
  const [moveTo, setMoveTo] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await store.CompleteSprint(projectEntity.id, idOf(sprint), {
        project: projectEntity,
        sprint: sprint.entity!,
        move_incomplete_to: moveTo ? otherSprints.find((s) => idOf(s) === moveTo)?.entity : undefined,
        retrospective_notes: notes || undefined,
      } as never);
      onDone();
    } catch {
      /* store surfaces error */
    } finally {
      setBusy(false);
    }
  };

  return (
    <ProjectModal
      open
      title={t('project.sprints.completeTitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={busy}>
            {t('project.sprints.complete')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {!!store.error && <ErrorAlert error={store.error} />}
        <p className="text-sm text-muted-foreground">{t('project.sprints.completeHint')}</p>
        <Field label={t('project.sprints.moveIncomplete')} htmlFor="complete-move">
          <Select id="complete-move" value={moveTo} onChange={(e) => setMoveTo(e.target.value)} aria-label={t('project.sprints.moveIncomplete')}>
            <option value="">{t('project.sprints.moveToBacklog')}</option>
            {otherSprints.map((s) => (
              <option key={idOf(s)} value={idOf(s)}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('project.sprints.retroNotes')} htmlFor="complete-notes">
          <Textarea id="complete-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        </Field>
      </div>
    </ProjectModal>
  );
}
